/**
 * Custom hook for managing dynamic chat input placeholders
 * Handles base state (page-aware) and re-engagement prompts
 * Note: Session progress is now handled by useSessionProgressToast (floating toast)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  TLDRData,
  getPrimaryConcept,
  pickRandom
} from '../utils/conceptExtractor';

// Timer constants
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;           // 5 minutes
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;          // Check every 30 sec
const REENGAGE_DISPLAY_DURATION_MS = 60 * 1000;    // 60 seconds

// Placeholder templates
const BASE_TEMPLATES_WITH_CONCEPT = [
  'Ask about {concept}...',
  'Curious about {concept}?',
  "What's unclear about {concept}?",
  'Need help with {concept}?',
  'Wondering about {concept}?'
];

const GENERIC_PLACEHOLDERS = [
  'Ask me anything...',
  'What can I help with?',
  'Curious about something?'
];

const REENGAGE_TEMPLATES = [
  "Back to {concept}? Ask if anything's unclear.",
  'Picking up {concept}? Let me know if you need help.',
  'Still working on {concept}? I can break it down.',
  'Returning to {concept} — want a refresher?'
];

export interface UseDynamicPlaceholderOptions {
  currentPage: number;
  pagesVisited: number[];
  tldrCache: Map<number, TLDRData>;
  currentTldrData: TLDRData | null;
  isUserTyping: boolean;
  isReady: boolean;  // Only start timers after book loads
}

export interface UseDynamicPlaceholderReturn {
  placeholder: string;
  currentConcept: string | null;
  isIdle: boolean;
}

type OverrideType = 'none' | 'reengage';

export function useDynamicPlaceholder({
  currentPage,
  pagesVisited,
  tldrCache,
  currentTldrData,
  isUserTyping,
  isReady
}: UseDynamicPlaceholderOptions): UseDynamicPlaceholderReturn {
  // State
  const [basePlaceholder, setBasePlaceholder] = useState<string>('Ask me anything...');
  const [overridePlaceholder, setOverridePlaceholder] = useState<string | null>(null);
  const [overrideType, setOverrideType] = useState<OverrideType>('none');
  const [isIdle, setIsIdle] = useState(false);

  // Refs for tracking without re-renders
  const lastActivityRef = useRef<number>(0);
  const isIdleRef = useRef<boolean>(false);
  const wasReadyRef = useRef<boolean>(false);
  const idleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const overrideExpiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownReengageForPageRef = useRef<Set<number>>(new Set());
  const currentPageRef = useRef<number>(currentPage);
  const tldrCacheRef = useRef<Map<number, TLDRData>>(tldrCache);

  // Keep refs in sync (pagesVisited not needed for placeholder but kept for interface compatibility)
  useEffect(() => {
    currentPageRef.current = currentPage;
    tldrCacheRef.current = tldrCache;
  }, [currentPage, pagesVisited, tldrCache]);

  // Initialize timestamps when isReady becomes true (book has loaded)
  useEffect(() => {
    if (isReady && !wasReadyRef.current) {
      const now = Date.now();
      lastActivityRef.current = now;
      wasReadyRef.current = true;
      console.log('[DynamicPlaceholder] Book loaded, starting timers');
    }
  }, [isReady]);

  // Get current concept
  const currentConcept = useMemo(() => {
    return getPrimaryConcept(currentTldrData);
  }, [currentTldrData]);

  // Generate base placeholder for current page
  const generateBasePlaceholder = useCallback((concept: string | null): string => {
    if (concept) {
      const template = pickRandom(BASE_TEMPLATES_WITH_CONCEPT) || BASE_TEMPLATES_WITH_CONCEPT[0];
      return template.replace('{concept}', concept);
    }
    return pickRandom(GENERIC_PLACEHOLDERS) || GENERIC_PLACEHOLDERS[0];
  }, []);

  // Clear override state
  const clearOverride = useCallback(() => {
    setOverridePlaceholder(null);
    setOverrideType('none');
    if (overrideExpiryTimerRef.current) {
      clearTimeout(overrideExpiryTimerRef.current);
      overrideExpiryTimerRef.current = null;
    }
  }, []);

  // Trigger re-engage placeholder (when user returns from idle)
  const triggerReengage = useCallback(() => {
    // Only trigger once per page
    if (hasShownReengageForPageRef.current.has(currentPageRef.current)) {
      return;
    }

    const concept = getPrimaryConcept(tldrCacheRef.current.get(currentPageRef.current) || null);
    if (!concept) {
      // Use generic reengage if no concept
      const template = pickRandom(REENGAGE_TEMPLATES) || REENGAGE_TEMPLATES[0];
      setOverridePlaceholder(template.replace('{concept}', 'this topic'));
    } else {
      const template = pickRandom(REENGAGE_TEMPLATES) || REENGAGE_TEMPLATES[0];
      setOverridePlaceholder(template.replace('{concept}', concept));
    }

    setOverrideType('reengage');
    hasShownReengageForPageRef.current.add(currentPageRef.current);

    // Auto-clear after duration
    if (overrideExpiryTimerRef.current) {
      clearTimeout(overrideExpiryTimerRef.current);
    }
    overrideExpiryTimerRef.current = setTimeout(() => {
      clearOverride();
    }, REENGAGE_DISPLAY_DURATION_MS);
  }, [clearOverride]);

  // Record user activity
  const recordActivity = useCallback(() => {
    const wasIdle = isIdleRef.current;
    lastActivityRef.current = Date.now();

    if (wasIdle) {
      isIdleRef.current = false;
      setIsIdle(false);
      // Trigger re-engage when coming back from idle
      triggerReengage();
    }
  }, [triggerReengage]);

  // Update base placeholder when page or concept changes
  useEffect(() => {
    // Clear any active override when navigating
    clearOverride();

    // Generate new base placeholder
    setBasePlaceholder(generateBasePlaceholder(currentConcept));
  }, [currentPage, currentConcept, generateBasePlaceholder, clearOverride]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousemove', 'click', 'scroll', 'keypress'];

    const handleActivity = () => {
      recordActivity();
    };

    // Tab visibility handling
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check if was idle
        const idleTime = Date.now() - lastActivityRef.current;
        if (idleTime >= IDLE_THRESHOLD_MS && !isIdleRef.current) {
          isIdleRef.current = true;
          setIsIdle(true);
        }
        // If idle and now active, the next activity event will trigger reengage
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recordActivity]);

  // Set up idle check interval - only when isReady
  useEffect(() => {
    if (!isReady) return;

    const checkIdle = () => {
      // Only check if tab is visible and timestamps are initialized
      if (document.visibilityState !== 'visible') return;
      if (lastActivityRef.current === 0) return;

      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime >= IDLE_THRESHOLD_MS && !isIdleRef.current) {
        isIdleRef.current = true;
        setIsIdle(true);
      }
    };

    idleCheckIntervalRef.current = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);

    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [isReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
      if (overrideExpiryTimerRef.current) {
        clearTimeout(overrideExpiryTimerRef.current);
      }
    };
  }, []);

  // Determine final placeholder based on priority
  // Priority: reengage > base
  // Don't change placeholder while user is typing
  const placeholder = useMemo(() => {
    if (isUserTyping) {
      // Return current value to prevent change during typing
      return overridePlaceholder || basePlaceholder;
    }

    if (overrideType === 'reengage' && overridePlaceholder) {
      return overridePlaceholder;
    }

    return basePlaceholder;
  }, [isUserTyping, overrideType, overridePlaceholder, basePlaceholder]);

  return {
    placeholder,
    currentConcept,
    isIdle
  };
}
